import React, { useEffect, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import Select, { createFilter } from "react-select";
import {
  Alert,
  Button,
  Card,
  Column,
  EmptyState,
  Icon,
  PageHeader,
  Select as AdminSelect,
  Skeleton,
  Table,
  styles as ui,
  useToast,
} from "stories/Admin";
import styles from "./prayerTimes.module.scss";
import useFetch from "hooks/useFetch";
import { adminSelectStyles } from "../adminSelectStyles";

interface PrayerTimes {
  date: string;
  fajr: string;
  fajr_endtime: string;
  duhr: string;
  asr: string;
  maghrib: string;
  isha: string;
  asr_1x_shadow: string;
  asr_2x_shadow: string;
  hijriDate: string;
}

interface PrayerTimeTable {
  id: number;
  name: string;
}

interface Location {
  id: number;
  name: string;
  prayerTimesTables: PrayerTimeTable[];
}

const MawaqeetPrayerTimes: React.FC = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [prayerTimesData, setPrayerTimesData] = useState<PrayerTimes[]>([]);
  const [yearPrayerTimesData, setYearPrayerTimesData] = useState<PrayerTimes[]>(
    [],
  );
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [isCsvLoading, setIsCsvLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const selectedTableId = 2;

  const toast = useToast();

  const { execute, isLoading } = useFetch<PrayerTimes[]>();
  const { execute: fetchLocations } = useFetch<Location[]>();

  // Fetch locations for dropdown
  useEffect(() => {
    const fetchLocationData = async () => {
      const result = await fetchLocations("/api/v1/locations", "GET");
      if (result) {
        const filteredLocations = result
          .filter((location) =>
            location.prayerTimesTables.some((table) => table.id === 2),
          )
          .sort((a, b) => a.name.localeCompare(b.name));

        setLocations(filteredLocations);
      }
    };
    fetchLocationData();
  }, []);

  const fetchPrayerTimes = async () => {
    if (!selectedLocationId) return;

    const month = (currentMonth.getMonth() + 1).toString().padStart(2, "0");
    const endpoint = `/api/v1/prayerTimes/${selectedLocationId}/${selectedTableId}/${currentYear}/${month}`;

    try {
      const result = await execute(endpoint, "GET");
      if (result?.length === 0) {
        setError("Ingen tilgjengelige bønnetider for den valgte perioden.");
        setPrayerTimesData([]);
      } else {
        setPrayerTimesData(result || []);
        setError(null);
      }
    } catch (err) {
      setPrayerTimesData([]);
      setError(
        "Kunne ikke hente bønnetider for den valgte lokasjonen og perioden. Vennligst prøv igjen.",
      );
    }
  };

  const fetchYearPrayerTimes = async () => {
    if (!selectedLocationId || currentYear === null) return;

    const endpoint = `/api/v1/prayerTimes/${selectedLocationId}/${selectedTableId}/${currentYear}`;
    setIsCsvLoading(true);
    try {
      const result = await execute(endpoint, "GET");
      setYearPrayerTimesData(result || []);
      setIsCsvLoading(false);
    } catch (err) {
      setYearPrayerTimesData([]);
      setError(
        "Kunne ikke hente bønnetider for den valgte lokasjonen og tabellen.",
      );
      setIsCsvLoading(false);
    }
  };

  const validateCsv = async (file: File): Promise<boolean> => {
    const expectedHeaders = [
      "date",
      "hijriDate",
      "fajr",
      "fajr_endtime",
      "duhr",
      "asr",
      "asr_1x_shadow",
      "asr_2x_shadow",
      "maghrib",
      "isha",
      "locationId",
      "prayerTimeTable",
    ];

    const text = await file.text();
    const rows = text.split("\n");
    const headers = rows[0]?.split(";");

    for (let i = 0; i < headers.length; i++) {
      if (headers[i]?.trim() !== expectedHeaders[i]) {
        toast.error(
          `Feil i CSV-filen: forventet kolonnen «${expectedHeaders[i]}», men fant «${headers[i]?.trim()}».`,
        );
        return false;
      }
    }

    return true;
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const isValid = await validateCsv(file);
      if (!isValid) return;

      const formData = new FormData();
      formData.append("file", file);

      try {
        const endpoint = `/api/v1/prayerTimes/${selectedLocationId}/${selectedTableId}/${currentYear}/upload-csv`;
        await execute(endpoint, "POST", formData);
        toast.success("CSV-filen er lastet opp.");
        fetchPrayerTimes();
      } catch (err) {
        toast.error("Kunne ikke laste opp CSV-filen.");
      }
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  useEffect(() => {
    if (selectedLocationId) {
      fetchPrayerTimes();
    }
  }, [selectedLocationId, currentMonth, currentYear]);

  const formatCsvData = (data: PrayerTimes[]) => {
    return data.map((row) => ({
      date: formatDateToDDMMYYYY(row.date),
      hijriDate: row.hijriDate,
      fajr: row.fajr,
      fajr_endtime: row.fajr_endtime,
      duhr: row.duhr,
      asr: row.asr,
      asr_1x_shadow: row.asr_1x_shadow,
      asr_2x_shadow: row.asr_2x_shadow,
      maghrib: row.maghrib,
      isha: row.isha,
      locationId: selectedLocationId,
      prayerTimeTable: selectedTableId,
    }));
  };

  const formatDateToDDMMYYYY = (dateString: string): string => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleLocationChange = async (selectedOption: any) => {
    const locationId = selectedOption?.value || null;
    setSelectedLocationId(locationId);

    if (locationId) {
      try {
        const endpoint = `/api/v1/prayerTimes/${locationId}/${selectedTableId}/${currentYear}`;
        setIsCsvLoading(true);
        const result = await execute(endpoint, "GET");
        setYearPrayerTimesData(result || []);
        setError(null);
      } catch (err) {
        setYearPrayerTimesData([]);
        setError(
          "Kunne ikke hente bønnetider for den valgte lokasjonen og tabellen.",
        );
      } finally {
        setIsCsvLoading(false);
      }
    }
  };

  const locationOptions = locations.map((location) => ({
    value: location.id,
    label: location.name,
  }));

  const customFilter = createFilter({
    ignoreCase: true,
    ignoreAccents: true,
    matchFrom: "any",
    stringify: (option) => option.label,
  });

  const monthName = (index: number) => {
    const name = new Intl.DateTimeFormat("nb-NO", { month: "long" }).format(
      new Date(2000, index, 1),
    );
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  const selectedLocationName = locations.find(
    (location) => location.id === selectedLocationId,
  )?.name;

  const columns: Array<Column<PrayerTimes>> = [
    {
      key: "date",
      header: "Dato",
      render: (times) => (
        <b>
          {new Intl.DateTimeFormat("nb-NO", {
            day: "numeric",
            month: "short",
          }).format(new Date(times.date))}
        </b>
      ),
    },
    {
      key: "hijri",
      header: "Hijri",
      numeric: true,
      render: (times) => (
        <span className={styles.hijri}>{times.hijriDate}</span>
      ),
    },
    { key: "fajr", header: "Fajr", numeric: true, render: (t) => t.fajr },
    {
      key: "fajr_endtime",
      header: "Soloppgang",
      numeric: true,
      render: (t) => t.fajr_endtime,
    },
    { key: "duhr", header: "Dhuhr", numeric: true, render: (t) => t.duhr },
    { key: "asr", header: "Asr", numeric: true, render: (t) => t.asr },
    {
      key: "asr1",
      header: "Asr 1x",
      numeric: true,
      render: (t) => t.asr_1x_shadow,
    },
    {
      key: "asr2",
      header: "Asr 2x",
      numeric: true,
      render: (t) => t.asr_2x_shadow,
    },
    {
      key: "maghrib",
      header: "Maghrib",
      numeric: true,
      render: (t) => t.maghrib,
    },
    { key: "isha", header: "Isha", numeric: true, render: (t) => t.isha },
  ];

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className={styles.skeletonRows}>
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} height={22} />
          ))}
        </div>
      );
    }
    if (error) {
      return <Alert tone="error">{error}</Alert>;
    }
    if (!(selectedLocationId && currentYear && currentMonth)) {
      return (
        <EmptyState icon="pin" title="Velg en lokasjon">
          Velg lokasjon, måned og år for å se bønnetidene.
        </EmptyState>
      );
    }
    if (prayerTimesData.length === 0) {
      return (
        <EmptyState icon="clock" title="Ingen bønnetider">
          Ingen tilgjengelige bønnetider for den valgte perioden.
        </EmptyState>
      );
    }
    return (
      <Table
        columns={columns}
        rows={prayerTimesData}
        rowKey={(times) => times.date}
        caption="Bønnetider"
      />
    );
  };

  const hasSelection = !!(selectedLocationId && currentYear && currentMonth);

  return (
    <>
      <PageHeader
        title="Bønnetider"
        subtitle="Mawaqeet-tabellen (#2) for hver lokasjon."
        actions={
          <>
            <Button
              variant="secondary"
              icon={<Icon name="upload" size={16} strokeWidth={2} />}
              onClick={triggerFileInput}
              disabled={!hasSelection || isUploading}
            >
              {isUploading ? "Laster opp …" : "Importer CSV"}
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept=".csv"
              onChange={handleFileUpload}
            />
            {hasSelection && !isCsvLoading && yearPrayerTimesData.length > 0 ? (
              <CSVLink
                data={formatCsvData(yearPrayerTimesData)}
                filename={`prayer_times_${selectedLocationId}_${currentYear}.csv`}
                className={`${ui.btn} ${ui.secondary}`}
              >
                <Icon name="download" size={16} strokeWidth={2} />
                Eksporter CSV for {currentYear}
              </CSVLink>
            ) : (
              <Button
                variant="secondary"
                icon={<Icon name="download" size={16} strokeWidth={2} />}
                loading={hasSelection && isCsvLoading}
                disabled
              >
                {hasSelection && isCsvLoading
                  ? "Laster ned …"
                  : hasSelection
                    ? "Ingen data å eksportere"
                    : `Eksporter CSV for ${currentYear}`}
              </Button>
            )}
          </>
        }
      />

      <Card>
        <div className={styles.filters}>
          <div className={ui.fld}>
            <label htmlFor="locationSearch" className={ui.lbl}>
              Lokasjon
            </label>
            <Select
              inputId="locationSearch"
              options={locationOptions}
              onChange={handleLocationChange}
              placeholder="Søk og velg lokasjon"
              filterOption={customFilter}
              noOptionsMessage={() => "Ingen lokasjoner funnet"}
              isClearable
              styles={adminSelectStyles<{ value: number; label: string }>()}
              menuPortalTarget={document.body}
            />
            <span className={ui.help}>
              Bare lokasjoner som har Mawaqeet-tabellen.
            </span>
          </div>
          <AdminSelect
            id="monthSelect"
            label="Måned"
            value={currentMonth.getMonth()}
            onChange={(e) =>
              setCurrentMonth((prev) => {
                const newMonth = new Date(prev);
                newMonth.setMonth(parseInt(e.target.value));
                return newMonth;
              })
            }
          >
            {Array.from({ length: 12 }, (_, index) => (
              <option key={index} value={index}>
                {monthName(index)}
              </option>
            ))}
          </AdminSelect>
          <AdminSelect
            id="yearSelect"
            label="År"
            value={currentYear}
            onChange={(e) => setCurrentYear(parseInt(e.target.value))}
          >
            {Array.from({ length: 7 }, (_, index) => {
              const year = new Date().getFullYear() + index;
              return (
                <option key={year} value={year}>
                  {year}
                </option>
              );
            })}
          </AdminSelect>
        </div>
      </Card>

      <Card
        title={
          hasSelection && selectedLocationName
            ? `${selectedLocationName} · ${monthName(currentMonth.getMonth())} ${currentYear}`
            : "Bønnetider"
        }
      >
        {renderBody()}
      </Card>

      <Alert tone="info">
        CSV-import må ha kolonnene date; hijriDate; fajr; fajr_endtime; duhr;
        asr; asr_1x_shadow; asr_2x_shadow; maghrib; isha; locationId;
        prayerTimeTable, skilt med semikolon.
      </Alert>
    </>
  );
};

export default MawaqeetPrayerTimes;
